import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

interface UserProfile {
  name: string;
  availableInvoiceBalance?: number;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  profile: UserProfile | null;
  login: () => void;
  logout: () => void;
  updateInvoiceQuota: (quota: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const authUuid = Cookies.get('auth_uuid');
    
    if (!authUuid) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('https://dashboard.invoice-qr.com/.netlify/functions/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid: authUuid }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setProfile(data.profile || null);
      } else {
        Cookies.remove('auth_uuid');
        setError('Authentication failed');
      }
    } catch (err) {
      setError('Failed to verify authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const login = () => {
    window.location.href = 'https://dashboard.invoice-qr.com/?redirect=https://pay.invoice-qr.com';
  };

  const logout = () => {
    Cookies.remove('auth_uuid');
    setIsAuthenticated(false);
    setProfile(null);
  };

  const updateInvoiceQuota = (quota: number) => {
    setProfile(prev => prev ? { ...prev, availableInvoiceBalance: quota } : null);
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      error, 
      profile, 
      login, 
      logout,
      updateInvoiceQuota 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}